import { Component, inject, TemplateRef, viewChild } from '@angular/core';
import { Observable } from 'rxjs';
import { ChargingUserStatus } from 'src/app/shared/enums/charging-user-status.enum';
import { ChargingUser } from '../charging-user.model';
import { Organization } from '../../organization/organization-models';
import { IdTagUserType } from 'src/app/shared/enums/id-tag-user-type.enum';
import { EnvironmentLoaderService } from 'src/app/shared/services/environment-loader.service';
import { CardComponent, ExpandListComponent, ExpandListConfiguration, ScreenSizeEnum } from 'ok-angular-shared/components';
import { RequiresFeatureRoleDirective } from '../../../shared/directives/requires-feature-role.directive';
import { TranslateChargingUserStatusEnum } from '../../../shared/pipes/translate-charging-user-status-enum.pipe';
import { TranslateIdTagUserTypeEnum } from '../../../shared/pipes/translate-id-tag-user-type-enum.pipe';
import { Department } from '../../department/department.model';
import { TextSearchComponent } from 'src/app/shared/components/text-search/text-search.component';
import { AdmOrgTypeaheadComponent } from 'src/app/shared/components/adm-org-typeahead/adm-org-typeahead.component';
import { AdmDepTypeaheadComponent } from 'src/app/shared/components/adm-dep-typeahead/adm-dep-typeahead.component';
import { ChargingUserListStateService } from './charging-user-list.state.service';
import { PagerBase } from 'src/app/shared/models/pagerBase';
import { AdminEnumSelectListComponent } from 'src/app/shared/components/admin-enum-select-list/admin-enum-select-list.component';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuTrigger, MatMenuItem } from '@angular/material/menu';
import { MatButton } from '@angular/material/button';
import { Permission } from 'src/app/shared/enums/permission.enum';
import { DatePipe } from '@angular/common';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { ChargingUserDetailsComponent } from '../charging-user-details/charging-user-details.component';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslateChargingUserAppUserPipe } from 'src/app/shared/pipes/translate-charging-user-app-user.pipe';
import { RouterLink } from '@angular/router';
import { AuthService } from 'src/app/core/auth/auth.service';
import { isAnyEnumStatus, isEnumStatus } from 'src/app/shared/utilities/enum-utility';
import { ChargingUsersOverviewDialogService } from './charging-users-overview-dialog.service';

@Component({
  selector: 'adm-charging-users-overview',
  templateUrl: './charging-users-overview.component.html',
  styleUrls: ['./charging-users-overview.component.scss'],
  imports: [
    RequiresFeatureRoleDirective,
    TextSearchComponent,
    AdmOrgTypeaheadComponent,
    AdmDepTypeaheadComponent,
    AdminEnumSelectListComponent,
    MatIcon,
    MatMenu,
    MatMenuTrigger,
    MatMenuItem,
    MatButton,
    CardComponent,
    ExpandListComponent,
    DatePipe,
    MatPaginator,
    ChargingUserDetailsComponent,
    MatTooltip,
    TranslateChargingUserStatusEnum,
    TranslateChargingUserAppUserPipe,
    RouterLink,
    TranslateIdTagUserTypeEnum,
  ],
  providers: [
    ChargingUserListStateService,
    ChargingUsersOverviewDialogService,
    TranslateChargingUserStatusEnum,
    TranslateIdTagUserTypeEnum,
  ],
})
export class ChargingUsersOverviewComponent extends PagerBase {
  translateChargingUserStatusEnum = inject(TranslateChargingUserStatusEnum);
  translateIdTagUserTypeEnum = inject(TranslateIdTagUserTypeEnum);
  state = inject(ChargingUserListStateService);
  dialogService = inject(ChargingUsersOverviewDialogService);
  authService = inject(AuthService);
  private environmentLoaderService = inject(EnvironmentLoaderService);

  public roles = this.environmentLoaderService.virtualRoles;
  public headerColumnStatusTemplate = viewChild<TemplateRef<ChargingUserDetailsComponent>>('headerColumnStatusTemplate');
  public columnAppTemplate = viewChild<TemplateRef<ChargingUserDetailsComponent>>('columnAppTemplate');
  public columnOrganizationTemplate = viewChild<TemplateRef<ChargingUserDetailsComponent>>('columnOrganizationTemplate');
  public columnCreatedTemplate = viewChild<TemplateRef<ChargingUserDetailsComponent>>('columnCreatedTemplate');
  public columnUpdatedTemplate = viewChild<TemplateRef<ChargingUserDetailsComponent>>('columnUpdatedTemplate');
  public columnCompanyCarTemplate = viewChild<TemplateRef<ChargingUserDetailsComponent>>('columnCompanyCarTemplate');
  public columnAccessGroupTemplate = viewChild<TemplateRef<ChargingUserDetailsComponent>>('columnAccessGroupTemplate');
  public Permission = Permission;
  public chargingUserStatus: ChargingUserStatus[] = Object.values(ChargingUserStatus);
  public idTagUserType: IdTagUserType[] = Object.values(IdTagUserType);
  public chargingUserStatusEnum = ChargingUserStatus;

  private readonly canChargingUserWrite = this.authService.hasRole(this.roles[Permission.ChargingUserWrite]);
  private readonly canChargingUserAppInvitationCreate = this.authService.hasRole(this.roles[Permission.ChargingUserAppInvitationCreate]);
  private readonly canChargingUserIdTagWrite = this.authService.hasRole(this.roles[Permission.ChargingUserIdTagWrite]);
  private readonly canChargingUserAccessGroupWrite = this.authService.hasRole(this.roles[Permission.ChargingUserAccessGroupWrite]);
  private readonly canChargingUserOrganizationSwitch = this.authService.hasRole(this.roles[Permission.ChargingUserOrganizationSwitch]);

  public config = (): ExpandListConfiguration<ChargingUser> => ({
    textLineClamp: 2,
    columns: [
      {
        field: 'chargingUserStatus',
        header: '',
        width: '120px',
        displayTemplate: this.headerColumnStatusTemplate(),
        disableSort: true,
      },
      { field: 'name', header: 'Navn', width: '1.5fr', sortField: 'name' },
      {
        field: 'organizationName',
        header: 'Organisation/afdeling',
        width: '1fr',
        sortField: 'organizationName',
        displayTemplate: this.columnOrganizationTemplate(),
      },
      {
        field: 'accessGroupCount',
        header: 'Adgangsgrupper',
        width: '1fr',
        sortField: 'accessGroupCount',
        displayTemplate: this.columnAccessGroupTemplate(),
      },
      {
        field: 'created',
        header: 'Oprettet',
        width: '1fr',
        sortField: 'created',
        visibleFrom: ScreenSizeEnum.md,
        displayTemplate: this.columnCreatedTemplate(),
      },
      {
        field: 'updated',
        header: 'Ændret',
        width: '1fr',
        sortField: 'updated',
        visibleFrom: ScreenSizeEnum.md,
        displayTemplate: this.columnUpdatedTemplate(),
      },
      {
        field: 'idTagUserType',
        header: 'Firmabil',
        width: '1fr',
        sortField: 'idTagUserType',
        visibleFrom: ScreenSizeEnum.md,
        displayTemplate: this.columnCompanyCarTemplate(),
      },
    ],
    classFn: (user: ChargingUser) => {
      if (user.highlight?.getValue()) {
        return 'highlight';
      }
      return '';
    },
    hideActionsFn: () => {
      return (
        !this.canChargingUserWrite &&
        !this.canChargingUserAppInvitationCreate &&
        !this.canChargingUserIdTagWrite &&
        !this.canChargingUserAccessGroupWrite &&
        !this.canChargingUserOrganizationSwitch
      );
    },
    actions: [
      {
        label: 'Gennemfør manuel behandling',
        icon: { name: 'user-s' },
        action: (user?: ChargingUser) => user && this.dialogService.processChargingUser(user),
        hiddenFn: (user?: ChargingUser) =>
          !user ||
          isAnyEnumStatus(user.chargingUserStatus, [ChargingUserStatus.Active, ChargingUserStatus.Deleted], this.chargingUserStatusEnum) ||
          !this.canChargingUserIdTagWrite,
      },
      {
        label: 'Rediger bruger',
        icon: { name: 'edit-s' },
        action: (user?: ChargingUser) => user && this.dialogService.editChargingUser(user),
        hiddenFn: (user?: ChargingUser) =>
          !user ||
          !isEnumStatus(user.chargingUserStatus, ChargingUserStatus.Active, this.chargingUserStatusEnum) ||
          !this.canChargingUserWrite,
      },
      {
        label: 'Rediger brikker',
        icon: { name: 'token-s' },
        action: (user?: ChargingUser) => user && this.dialogService.idTags(user),
        hiddenFn: (user?: ChargingUser) =>
          !user ||
          !isEnumStatus(user.chargingUserStatus, ChargingUserStatus.Active, this.chargingUserStatusEnum) ||
          !this.canChargingUserIdTagWrite,
      },
      {
        label: 'Rediger adgangsgrupper',
        icon: { name: 'users-s' },
        action: (user?: ChargingUser) => user && this.dialogService.accessGroups(user),
        hiddenFn: (user?: ChargingUser) =>
          !user ||
          !isAnyEnumStatus(user.chargingUserStatus, [ChargingUserStatus.Active, ChargingUserStatus.Created], this.chargingUserStatusEnum) ||
          !this.canChargingUserAccessGroupWrite,
      },
      {
        label: 'Tidligere adgangsgrupper',
        icon: { name: 'users-s' },
        action: (user?: ChargingUser) => user && this.dialogService.previousAccessGroups(user),
        hiddenFn: (user?: ChargingUser) => !user || user.previousAccessGroupCount === 0,
      },
      {
        label: 'Inviter til OK-App',
        icon: { name: 'smartphone-s' },
        action: (user?: ChargingUser) => user && this.dialogService.openAppInviteIdTagUser(user),
        hiddenFn: (user?: ChargingUser) =>
          !user ||
          !isEnumStatus(user.chargingUserStatus, ChargingUserStatus.Active, this.chargingUserStatusEnum) ||
          user.accessGroupCount !== 0 ||
          !this.canChargingUserAppInvitationCreate,
      },
      {
        label: 'Send invitation til OK-appen',
        icon: 'smartphone',
        action: (user?: ChargingUser) => user && this.dialogService.sendAppInvitation(user),
        hiddenFn: (user?: ChargingUser) =>
          !user ||
          isEnumStatus(user.chargingUserStatus, ChargingUserStatus.Deleted, this.chargingUserStatusEnum) ||
          user.accessGroupCount === 0 ||
          !this.canChargingUserAppInvitationCreate,
      },
      {
        label: 'Flyt organisation',
        icon: { name: 'building-s' },
        action: (user?: ChargingUser) => user && this.dialogService.switchOrganization(user),
        hiddenFn: (user?: ChargingUser) =>
          !user ||
          user.chargingUserStatus.toString() !== this.chargingUserStatusEnum[this.chargingUserStatusEnum.Active] ||
          !this.canChargingUserOrganizationSwitch,
      },
      {
        label: 'Slet bruger',
        icon: { name: 'trash-s' },
        action: (user?: ChargingUser) => user && this.dialogService.deleteChargingUser(user),
        hiddenFn: (user?: ChargingUser) =>
          !user ||
          user.chargingUserStatus.toString() !== this.chargingUserStatusEnum[this.chargingUserStatusEnum.Active] ||
          !this.canChargingUserWrite,
      },
      {
        label: 'Slet bestilling',
        icon: 'delete_forever',
        action: (user?: ChargingUser) => user && this.dialogService.deleteChargingUser(user),
        hiddenFn: (user?: ChargingUser) =>
          !user ||
          user.chargingUserStatus.toString() !== this.chargingUserStatusEnum[this.chargingUserStatusEnum.Pending] ||
          !this.canChargingUserWrite,
      },
    ],
  });

  public pageSwitch(event: PageEvent) {
    this.state.filters.update(x => ({
      ...x,
      page: event,
    }));
  }

  querySearchInput = (source: Observable<string>) => {
    source.subscribe(value => {
      this.state.filters.update(x => ({
        ...x,
        query: value && value.length > 0 ? value : null,
        page: { ...x.page, pageIndex: 0 },
      }));
    });
  };

  clearOrganizations = () => {
    this.state.resetDepartmentFilterSignal.set({ reset: true });
    this.state.filters.update(x => ({
      ...x,
      departmentId: null,
      page: { ...x.page, pageIndex: 0 },
    }));
  };

  organizationSelected = (org: Organization | null | undefined) => {
    this.state.filters.update(x => ({
      ...x,
      organizationId: org ? org.id : null,
      page: { ...x.page, pageIndex: 0 },
    }));
  };

  departmentSelected = (department: Department | null | undefined) => {
    this.state.filters.update(x => ({
      ...x,
      departmentId: department ? department.id : null,
      page: { ...x.page, pageIndex: 0 },
    }));
  };

  updateChargingUserStatus(value: ChargingUserStatus | null) {
    this.state.filters.update(f => ({ ...f, selectedChargingUserStatus: value, page: { ...f.page, pageIndex: 0 } }));
  }

  updateIdTagUserType(value: IdTagUserType | null) {
    this.state.filters.update(f => ({ ...f, selectedIdTagUserType: value, page: { ...f.page, pageIndex: 0 } }));
  }
}
